import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { ContentFitnessCoverageStatus } from "contracts/enums/content-fitness";
import type { HealthState } from "contracts/enums/core";
import { ISSUE_SEVERITY } from "contracts/enums/core";
import type { Pillar } from "contracts/enums/metrics";
import { PILLAR } from "contracts/enums/metrics";
import { CORE_WEB_VITALS_CONFIDENCE_LEVEL } from "contracts/enums/web-vitals";
import { METRIC_CATALOG } from "contracts/report/catalog";
import { CONTENT_FITNESS_DIMENSIONS } from "contracts/report/content-fitness/dimensions";
import { healthState, PAGE_THRESHOLDS } from "contracts/report/health";
import { pathWithoutQuery } from "../../boundaries/url-path.parser";
import { pluralize } from "../../ui/format";
import { isOpen } from "../website/issueModel";
import {
  type LocalePathContext,
  localeContentPathSegments,
  localePathContextForPages,
  routePathSegments,
} from "./boundaries/locale-path.parser";
import { clusterLabel } from "./estateFormat";
import { isImportantPath } from "./issuePageResolution";

export type OverviewPage = OverviewResponse["pages"][number];

export type ClusterContext = LocalePathContext;

export type PillarSignal = {
  key: string;
  label: string;
  score: number;
  state: HealthState;
  detail: string;
  description: string;
  coverageLabel: string | null;
  coverageStatus: ContentFitnessCoverageStatus | null;
  coverage: OverviewResponse["signals"][number]["coverage"];
};

// Resolve catalog signals; Content Fitness is limited to five canonical dimensions.
export function signalsForPillar(
  data: OverviewResponse,
  pillar: Pillar,
): PillarSignal[] {
  const byKey = new Map(data.signals.map((signal) => [signal.key, signal]));
  const metrics = METRIC_CATALOG.filter(
    (metric) =>
      metric.pillar === pillar &&
      (pillar !== PILLAR.CONTENT_FITNESS ||
        CONTENT_FITNESS_DIMENSIONS.some(
          (dimension) => dimension.key === metric.key,
        )),
  );
  return metrics.flatMap((metric) => {
    const signal = byKey.get(metric.key);
    if (!signal) {
      return [];
    }
    return [
      {
        key: metric.key,
        label: metric.label,
        score: signal.score,
        state: healthState(signal.score, metric.good, metric.watch),
        detail: signal.detail,
        description: metric.description,
        coverageLabel: signal.coverage
          ? `${signal.coverage.assessedSubcriteria}/${signal.coverage.canonicalSubcriteria} subcriteria scored`
          : null,
        coverageStatus: signal.coverage?.coverageStatus ?? null,
        coverage: signal.coverage,
      },
    ];
  });
}

export function contentFitnessHeadline(data: OverviewResponse) {
  const hasContentFitness =
    signalsForPillar(data, PILLAR.CONTENT_FITNESS).length > 0;
  const score = hasContentFitness ? data.signal.score : null;
  return {
    score,
    scoreDelta: score === null ? null : data.signal.scoreDelta,
    trend: score === null ? [] : data.signal.trend,
  };
}

type EstateCluster = {
  key: string;
  label: string;
  pages: OverviewPage[];
  averageScore: number | null;
  weakPages: number;
  importantPages: number;
};

export function pageHealthClass(score: number | null): HealthState {
  return healthState(score, PAGE_THRESHOLDS.good, PAGE_THRESHOLDS.watch);
}

export function pageContentFitnessScore(page: OverviewPage): number | null {
  return page.contentFitness?.score ?? null;
}

export function pageContentFitnessCoverageLabel(page: OverviewPage): string {
  const contentFitness = page.contentFitness;
  if (!contentFitness) {
    return "Not assessed";
  }
  if (contentFitness.score === null) {
    return "Assessed, not scored";
  }
  return `${contentFitness.scoredSubcriteria} of ${contentFitness.canonicalSubcriteria} subcriteria scored`;
}

/** The server-computed report coverage triple; its legacy `capturedPages`
 * field is the report inventory, which can include captured pages whose later
 * assessment did not complete. */
export function contentFitnessPageCoverage(data: OverviewResponse) {
  return (
    data.lastRun?.contentFitnessCoverage ?? {
      capturedPages: 0,
      assessedPages: 0,
      scoredPages: 0,
    }
  );
}

export function contentFitnessCoverageDetail(
  coverage: NonNullable<PillarSignal["coverage"]>,
): string {
  return `${coverage.scoredPages} of ${coverage.capturedPages} inventory ${pluralize(coverage.capturedPages, "page")} scored / ${coverage.assessedPages} assessed`;
}

export function importantPagePaths(data: OverviewResponse): Set<string> {
  const important = new Set<string>();
  for (const issue of data.issues.filter(isOpen)) {
    if (
      issue.severity !== ISSUE_SEVERITY.CRITICAL &&
      issue.severity !== CORE_WEB_VITALS_CONFIDENCE_LEVEL.HIGH
    ) {
      continue;
    }
    for (const page of issue.pages) {
      important.add(page.path);
      important.add(pathWithoutQuery(page.path));
    }
  }
  return important;
}

export function estateClusters(data: OverviewResponse): EstateCluster[] {
  const important = importantPagePaths(data);
  const clusterContext = clusterContextForPages(data.pages);
  const groups = new Map<string, OverviewPage[]>();
  for (const page of data.pages) {
    const key = clusterKey(page.path, clusterContext);
    groups.set(key, [...(groups.get(key) ?? []), page]);
  }

  return [...groups.entries()]
    .map(([key, pages]) => {
      const scores = pages
        .map(pageContentFitnessScore)
        .filter((score): score is number => score !== null);
      const averageScore =
        scores.length > 0
          ? Math.round(
              scores.reduce((sum, score) => sum + score, 0) / scores.length,
            )
          : null;
      return {
        key,
        label: clusterLabel(key, pages),
        pages,
        averageScore,
        weakPages: pages.filter((page) => {
          const score = pageContentFitnessScore(page);
          return score !== null && score < 60;
        }).length,
        importantPages: pages.filter((page) =>
          isImportantPath(important, page.path),
        ).length,
      };
    })
    .sort((left, right) => right.pages.length - left.pages.length);
}

export function estateStats(data: OverviewResponse) {
  const important = importantPagePaths(data);
  return {
    pageCount: data.pages.length,
    issueCount: data.issues.filter(isOpen).length,
    weakPageCount: data.pages.filter((page) => {
      const score = pageContentFitnessScore(page);
      return score !== null && score < 60;
    }).length,
    brokenTransitionCount: data.links.filter((link) => link.agentBroken).length,
    importantPageCount: data.pages.filter((page) =>
      isImportantPath(important, page.path),
    ).length,
    clusterCount: estateClusters(data).length,
  };
}

export function clusterContextForPages(
  pages: readonly { path: string }[],
): ClusterContext {
  return localePathContextForPages(pages);
}

export function contentPathSegments(
  path: string,
  context?: ClusterContext,
): string[] {
  return localeContentPathSegments(path, context);
}

export function clusterKey(path: string, context?: ClusterContext): string {
  return contentPathSegments(path, context)[0] ?? "home";
}

/** Stable route identity uses the first stored path segment without applying
 * estate presentation rules such as locale-shell stripping. */
export function routeClusterKey(path: string): string {
  return routePathSegments(path)[0] ?? "home";
}
